/**
 * API 配置和请求工具
 * 统一管理所有后端接口的基础URL和请求方法
 */
import { enqueuePendingAction, startPendingQueueProcessor } from "./offlineQueue";

// 根据环境动态设置基础URL，默认使用相对 /api 路径，避免 HTTPS 页面请求 HTTP 资源
const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

/**
 * 获取存储在localStorage中的认证令牌
 */
export const getAuthToken = () => {
  return localStorage.getItem("token");
};

/**
 * 带认证信息的 fetch 请求
 * @param {string} url - 相对路径（如 /users/me）
 * @param {object} options - fetch选项
 * @returns {Promise<Response>}
 */
export const fetchWithAuth = async (url, options = {}) => {
  const token = getAuthToken();
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers,
    });

    // 如果是 401，说明token无效
    if (response.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("isLoggedIn");
      window.location.href = "/login";
    }

    return response;
  } catch (error) {
    console.error("API请求失败:", error);
    throw error;
  }
};

/**
 * 发送FormData格式的请求（用于登录）
 * @param {string} url - 相对路径
 * @param {FormData} formData - 表单数据
 * @returns {Promise<Response>}
 */
export const fetchFormData = async (url, formData) => {
  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: "POST",
      body: formData,
    });
    return response;
  } catch (error) {
    console.error("API请求失败:", error);
    throw error;
  }
};

/**
 * 获取完整的API基础URL（用于静态资源等）
 */
export const getApiBaseUrl = () => {
  return API_BASE_URL;
};

/**
 * 获取完整的静态资源URL
 * @param {string} path - 相对路径（如 /static/word_1.png）
 * @returns {string} 完整URL
 */
export const getStaticUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  return `${API_BASE_URL}${path}`;
};

// 常见的API端点（已补充所有新增功能接口）
export const API_ENDPOINTS = {
  // 用户相关
  LOGIN: "/login",
  REGISTER: "/register",
  GET_USER: "/users/me",
  UPDATE_AVATAR: "/users/me/avatar",
  DELETE_USER: "/users/me",
  GET_AVATARS: "/avatars",

  // 单词相关
  LIST_WORDS: "/words",
  GET_WORD: (id) => `/words/${id}`,
  GET_RANDOM_WORD: "/words/random",
  IMPORT_WORDS: "/words/import",
  GENERATE_WORD_IMAGE: (id) => `/words/${id}/generate-image`,
  COMPLETE_WORD_INFO: (id) => `/words/${id}/complete-info`,  // 新增: AI补全单词详细信息
  
  // 收藏相关
  LIST_FAVORITES: "/favorites",
  ADD_FAVORITE: (id) => `/favorites/${id}`,
  REMOVE_FAVORITE: (id) => `/favorites/${id}`,

  // 学习记录/艾宾浩斯
  CHECK_IN: "/learning/check-in",
  CHECK_IN_STATUS: "/learning/check-in-status",
  NEXT_WORD: "/learning/next-word",
  RECORD_STUDY: "/learning/record",
  LEARNING_STATS: "/learning/stats",
  PROGRESS: "/learning/progress",
  
  // ✨ 新增: 艾宾浩斯复习计划表
  EBBINGHAUS_TABLE: "/learning/ebbinghaus-table",
  
  // ✨ 新增: 碎片相关
  FRAGMENT_STATS: "/fragments/stats",
  FRAGMENT_LIST: "/fragments",

  // 背景皮肤系统
  GET_BACKGROUNDS: "/backgrounds",
  SET_BACKGROUND: "/backgrounds/set",
  GET_CURRENT_BACKGROUND: "/backgrounds/current",
};
/**
 * 认证接口（登录、注册）
 */
export const authApi = {
  // 登录（用 FormData 格式）
  login: async (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.LOGIN}`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (response.ok && data.access_token) {
        localStorage.setItem('token', data.access_token);
      }
      return { ok: response.ok, data };
    } catch (error) {
      console.error('登录失败:', error);
      return { ok: false, error };
    }
  },
  
  // 注册
  register: async (username, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.REGISTER}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      return { ok: response.ok, data };
    } catch (error) {
      console.error('注册失败:', error);
      return { ok: false, data: null, error };
    }
  },
};

/**
 * 用户相关接口
 */
export const userApi = {
  // 获取用户信息
  getUserInfo: () => fetchWithAuth(API_ENDPOINTS.GET_USER).then(res => res.json()),
  
  // 更新头像
  updateAvatar: (avatar) => fetchWithAuth(API_ENDPOINTS.UPDATE_AVATAR, {
    method: 'PUT',
    body: JSON.stringify({ avatar })
  }).then(res => res.json()),
  
  // 删除账号
  deleteAccount: () => fetchWithAuth(API_ENDPOINTS.DELETE_USER, { method: 'DELETE' }),
  
  // 获取头像列表
  getAvatarList: () => fetchWithAuth(API_ENDPOINTS.GET_AVATARS).then(res => res.json()),
  
  // 获取学习计划
  getStudyPlan: () => fetchWithAuth('/users/me/study-plan').then(res => res.json()),
  
  // 更新学习计划
  updateStudyPlan: (dailyNewWordPlan) => fetchWithAuth('/users/me/study-plan', {
    method: 'PUT',
    body: JSON.stringify({ daily_new_word_plan: dailyNewWordPlan })
  }).then(res => res.json())
};

/**
 * 单词相关接口
 */
export const wordApi = {
  // 获取所有单词
  getWordList: (page = 1, size = 1000) => fetchWithAuth(
    `${API_ENDPOINTS.LIST_WORDS}?page=${page}&size=${size}`
  ).then(res => res.json()),

  // 批量导入单词到后端
  importWords: (batch) => fetchWithAuth(API_ENDPOINTS.IMPORT_WORDS, {
    method: 'POST',
    body: JSON.stringify(batch)
  }).then(res => res.json()),
  
  // 获取随机单词
  getRandomWord: () => fetchWithAuth(API_ENDPOINTS.GET_RANDOM_WORD).then(res => res.json()),
  
  // 生成单词图片
  generateWordImage: (wordId, force = false) => fetchWithAuth(
    `${API_ENDPOINTS.GENERATE_WORD_IMAGE(wordId)}?force=${force}`,
    { method: 'POST' }
  ).then(res => res.json()),
  
  // ✨ 新增: AI补全单词详细信息
  completeWordInfo: (wordId) => fetchWithAuth(
    API_ENDPOINTS.COMPLETE_WORD_INFO(wordId),
    { method: 'POST' }
  ).then(res => res.json()),
  
  // 收藏单词
  addFavorite: (wordId) => fetchWithAuth(API_ENDPOINTS.ADD_FAVORITE(wordId), { method: 'POST' }),
  
  // 取消收藏
  removeFavorite: (wordId) => fetchWithAuth(API_ENDPOINTS.REMOVE_FAVORITE(wordId), { method: 'DELETE' }),
  
  // 获取收藏列表
  getFavorites: () => fetchWithAuth(API_ENDPOINTS.LIST_FAVORITES).then(res => res.json())
};

/**
 * 学习/艾宾浩斯相关接口
 */
export const learningApi = {
  // 签到
  checkIn: () => fetchWithAuth(API_ENDPOINTS.CHECK_IN, { method: 'POST' }).then(res => res.json()),
  
  // 获取签到状态
  getCheckInStatus: () => fetchWithAuth(API_ENDPOINTS.CHECK_IN_STATUS).then(res => res.json()),
  
  // 获取下一个推荐单词
  getNextStudyWord: () => fetchWithAuth(API_ENDPOINTS.NEXT_WORD).then(res => res.json()),
  
  // 提交学习结果
  // 提交学习结果：网络失败时入队 pendingActions
  recordStudyResult: async (wordId, result, studyTime = 30) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.RECORD_STUDY, {
        method: 'POST',
        body: JSON.stringify({ word_id: wordId, result, study_duration_seconds: studyTime })
      });
      const data = await res.json();
      // 启动后台队列处理器（幂等，重复调用无害）
      try { startPendingQueueProcessor(); } catch (e) {}
      return data;
    } catch (err) {
      // 网络或请求失败，落盘待同步
      enqueuePendingAction({ url: API_ENDPOINTS.RECORD_STUDY, method: 'POST', body: { word_id: wordId, result, study_duration_seconds: studyTime } });
      try { startPendingQueueProcessor(); } catch (e) {}
      // 返回一个本地风格响应，方便前端继续工作
      return { ok: false, error: 'queued_offline' };
    }
  },
  
  // 获取学习统计
  getLearningStats: () => fetchWithAuth(API_ENDPOINTS.LEARNING_STATS).then(res => res.json())
  ,
  // 获取学习进度/待复习单词
  // 可选参数：query 字符串（如 ?bucket=今日到期）
  getProgress: (query = "") => fetchWithAuth(`${API_ENDPOINTS.PROGRESS}${query}`).then(res => res.json()),
  
  // ✨ 新增: 获取碎片统计
  getFragmentStats: () => fetchWithAuth(API_ENDPOINTS.FRAGMENT_STATS).then(res => res.json()),
  
  // ✨ 新增: 获取碎片列表
  getFragments: () => fetchWithAuth(API_ENDPOINTS.FRAGMENT_LIST).then(res => res.json()),
  
  //  新增: 获取艾宾浩斯复习计划表
  getEbbinghausTable: () => fetchWithAuth(API_ENDPOINTS.EBBINGHAUS_TABLE).then(res => res.json())
};

/**
 * 背景皮肤相关接口
 */
export const backgroundApi = {
  // 获取所有背景
  getAllBackgrounds: () => fetchWithAuth(API_ENDPOINTS.GET_BACKGROUNDS).then(res => res.json()),
  
  // 切换背景
  setCurrentBackground: (backgroundId) => fetchWithAuth(API_ENDPOINTS.SET_BACKGROUND, {
    method: 'POST',
    body: JSON.stringify({ background_id: backgroundId })
  }).then(res => res.json()),
  
  // 获取当前背景
  getCurrentBackground: () => fetchWithAuth(API_ENDPOINTS.GET_CURRENT_BACKGROUND).then(res => res.json())
};

/**
 * AI 聊天与翻译接口
 */
export const aiApi = {
  // 翻译成法语
  translateToFrench: (partOfSpeech, meaning) => fetchWithAuth('/translate/to-french', {
    method: 'POST',
    body: JSON.stringify({ part_of_speech: partOfSpeech, meaning })
  }).then(res => res.json()),
  
  // 与 AI 聊天
  chat: (message, sessionId = null) => fetchWithAuth('/chat', {
    method: 'POST',
    body: JSON.stringify({ message, session_id: sessionId })
  }).then(res => res.json())
};